import type {
  DateFormatId,
  EnvelopePoints,
  EnvelopePreset,
  TextCurve,
  TextElement,
  TextPath,
  TextPathPreset,
  TextTool,
  TextWarp,
  UnitPoint,
} from './types.js';

/**
 * The text model Customily's four tools need, kept out of the renderer so the
 * studio, the importer, the API and both preview paths all agree on one answer.
 *
 * Nothing here touches a canvas: shapes are described in a UNIT BOX (0..1 both
 * ways over the element's own box, y downward) and dates are formatted by
 * pattern. Both are things a merchant sees in two places at once — the editor
 * and the print file — and a second implementation is how those two drift.
 */

// ── Which tool authored a layer ──────────────────────────────────────────────

/**
 * The tool a text layer belongs to.
 *
 * Documents written before the tools existed carry no `tool`, so it is derived
 * from the geometry the layer actually has — the same order the renderer
 * branches in, so the panel the studio opens always matches what is drawn.
 */
export function textToolOf(el: Pick<TextElement, 'tool' | 'curve' | 'warp'>): TextTool {
  if (el.tool) return el.tool;
  if (el.curve) return el.curve.kind === 'path' ? 'path' : 'curved';
  if (el.warp) return 'warped';
  return 'box';
}

/**
 * The tiled artwork filling the glyphs, under either name: our own `pattern`
 * or Customily's `texture`. One reader so a texture is not silently dropped by
 * the half of the pipeline that only knows the other name.
 */
export function textPattern(
  el: Pick<TextElement, 'pattern' | 'texture'>,
): { assetId: string; scale?: number } | undefined {
  return el.pattern ?? el.texture;
}

// ── Fixed words and symbols ──────────────────────────────────────────────────

/**
 * Applies Customily's "Between Characters" and "Between Words" glue.
 *
 * Between-characters joins the graphemes of each WORD (never across a space,
 * or "A B" would print "A- -B"); between-words REPLACES the whitespace run
 * between words, so the merchant controls the spacing by typing it.
 *
 * Prefix and suffix are deliberately not this function's business: Customily
 * puts all four in one "Fixed Words & Symbols" block but only the two glues
 * reach inside the shopper's own value.
 */
export function applyTextGlue(
  text: string,
  glue: Pick<TextElement, 'betweenCharacters' | 'betweenWords'>,
): string {
  const between = glue.betweenCharacters ?? '';
  const words = glue.betweenWords;
  if (between === '' && (words === undefined || words === '')) return text;
  if (text === '') return text;

  // Split keeping the separators, so a text with no between-words glue keeps
  // the exact whitespace the shopper typed (two spaces, a newline).
  const parts = text.split(/(\s+)/u);
  return parts
    .map((part, i) => {
      if (i % 2 === 1) return words !== undefined && words !== '' ? words : part;
      return between === '' ? part : graphemes(part).join(between);
    })
    .join('');
}

/**
 * User-perceived characters. Splitting on code points instead would glue a
 * combining accent away from the letter it sits on.
 */
export function graphemes(text: string): string[] {
  if (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') {
    const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
    return Array.from(segmenter.segment(text), (s) => s.segment);
  }
  return [...text];
}

// ── Text along a path ────────────────────────────────────────────────────────

/**
 * One draggable handle of a text path, in unit coordinates. `control` marks a
 * bezier control point (the studio draws those as a handle on a dashed leader);
 * the rest are on-curve endpoints.
 */
export interface PathNode extends UnitPoint {
  control?: boolean;
}

const round = (n: number): number => Math.round(n * 1e4) / 1e4;

const d = (...parts: Array<string | number>): string =>
  parts.map((p) => (typeof p === 'number' ? String(round(p)) : p)).join(' ');

/**
 * The three preset shapes, in the unit box.
 *
 * `arc` is a quadratic whose control point is the apex — the shape the tool
 * opens with; `line` is the two-point flat case; `wave` is a cubic S-curve
 * point-symmetric about the box's middle, so its two humps read as one
 * undulation rather than a lopsided bulge.
 *
 * `line` is horizontal where Customily's default happens to be the diagonal of
 * its square default box; a flat baseline is what "Line" is asked for, and the
 * nodes are draggable either way.
 */
export function pathPreset(preset: 'arc' | 'line' | 'wave'): TextPath {
  switch (preset) {
    case 'line':
      return { kind: 'path', preset, d: d('M', 0, 0.5, 'L', 1, 0.5) };
    case 'wave':
      return { kind: 'path', preset, d: d('M', 0, 0.7, 'C', 0.3, 0.1, 0.7, 0.9, 1, 0.3) };
    case 'arc':
    default:
      return { kind: 'path', preset: 'arc', d: d('M', 0, 0.75, 'Q', 0.5, 0.1, 1, 0.75) };
  }
}

/** The `d` a curve actually carries: the unit-box path, or nothing. */
export function textPathData(curve: TextCurve | undefined): string | undefined {
  if (!curve || curve.kind !== 'path') return undefined;
  return curve.d && curve.d.trim() !== '' ? curve.d : undefined;
}

/**
 * The path's handles, in drawing order: the start point, then each segment's
 * control points followed by its endpoint.
 *
 * Round-trips with {@link pathFromNodes} for every shape the presets make, so
 * the studio can drag a node and write the result straight back.
 */
export function pathNodes(path: TextPath | TextCurve | string): PathNode[] {
  const source = typeof path === 'string' ? path : (textPathData(path) ?? '');
  const nodes: PathNode[] = [];
  let x = 0;
  let y = 0;
  let started = false;

  const tokens = source.match(/[MmLlHhVvCcSsQqTtZz]|[-+]?(?:\d*\.\d+|\d+\.?)(?:[eE][-+]?\d+)?/g) ?? [];
  let i = 0;
  let cmd = '';
  const next = (): number => Number(tokens[i++] ?? 0);
  while (i < tokens.length) {
    const token = tokens[i]!;
    if (/[A-Za-z]/.test(token)) {
      cmd = token;
      i++;
      if (cmd === 'Z' || cmd === 'z') continue;
    } else if (cmd === 'M') cmd = 'L';
    else if (cmd === 'm') cmd = 'l';
    if (cmd === '') break;

    const rel = cmd === cmd.toLowerCase();
    const ox = rel ? x : 0;
    const oy = rel ? y : 0;
    switch (cmd.toUpperCase()) {
      case 'M': {
        x = next() + ox;
        y = next() + oy;
        // A path made of several subpaths is not something the presets write;
        // the first one is the baseline, later moves just continue the list.
        nodes.push({ x, y });
        started = true;
        break;
      }
      case 'L': {
        x = next() + ox;
        y = next() + oy;
        nodes.push({ x, y });
        break;
      }
      case 'H': {
        x = next() + ox;
        nodes.push({ x, y });
        break;
      }
      case 'V': {
        y = next() + oy;
        nodes.push({ x, y });
        break;
      }
      case 'Q':
      case 'T': {
        if (cmd.toUpperCase() === 'Q') {
          nodes.push({ x: next() + ox, y: next() + oy, control: true });
        }
        x = next() + ox;
        y = next() + oy;
        nodes.push({ x, y });
        break;
      }
      case 'C':
      case 'S': {
        if (cmd.toUpperCase() === 'C') {
          nodes.push({ x: next() + ox, y: next() + oy, control: true });
        }
        nodes.push({ x: next() + ox, y: next() + oy, control: true });
        x = next() + ox;
        y = next() + oy;
        nodes.push({ x, y });
        break;
      }
      case 'A': {
        // Arcs are not authored here; skip the seven numbers and take the end.
        next(); next(); next(); next(); next();
        x = next() + ox;
        y = next() + oy;
        nodes.push({ x, y });
        break;
      }
      default:
        i = tokens.length;
    }
  }
  return started ? nodes : [];
}

/**
 * Rebuilds a path from dragged nodes. Two nodes make a line, three a quadratic,
 * four a cubic — exactly the presets' shapes — and anything longer is chained
 * cubics when the count fits, a polyline otherwise.
 */
export function pathFromNodes(
  nodes: readonly PathNode[],
  preset: TextPathPreset = 'custom',
): TextPath {
  if (nodes.length < 2) return pathPreset('line');
  const p = (n: PathNode, ...rest: Array<string | number>): Array<string | number> => [
    ...rest,
    round(n.x),
    round(n.y),
  ];
  const first = nodes[0]!;
  const parts: Array<string | number> = ['M', round(first.x), round(first.y)];

  if (nodes.length === 2) parts.push(...p(nodes[1]!, 'L'));
  else if (nodes.length === 3) parts.push(...p(nodes[2]!, 'Q', round(nodes[1]!.x), round(nodes[1]!.y)));
  else if ((nodes.length - 1) % 3 === 0) {
    for (let i = 1; i < nodes.length; i += 3) {
      parts.push(
        'C',
        round(nodes[i]!.x), round(nodes[i]!.y),
        round(nodes[i + 1]!.x), round(nodes[i + 1]!.y),
        round(nodes[i + 2]!.x), round(nodes[i + 2]!.y),
      );
    }
  } else {
    for (let i = 1; i < nodes.length; i++) parts.push(...p(nodes[i]!, 'L'));
  }
  return { kind: 'path', preset, d: parts.join(' ') };
}

// ── The warp envelope ────────────────────────────────────────────────────────

const rect = (): UnitPoint[] => [
  { x: 0, y: 0 },
  { x: 0.5, y: 0 },
  { x: 1, y: 0 },
  { x: 1, y: 0.5 },
  { x: 1, y: 1 },
  { x: 0.5, y: 1 },
  { x: 0, y: 1 },
  { x: 0, y: 0.5 },
];

/**
 * The eight envelope points of a preset, in the perimeter order
 * {@link EnvelopePoints} documents.
 *
 * Every shape is a move of the plain rectangle's handles and nothing else —
 * that IS Customily's mechanism, and it is why the tool has no strength
 * slider. Points may leave 0..1 (Bubble's midpoints do); the box is a frame of
 * reference, not a clamp.
 */
export function envelopePreset(name: EnvelopePreset): EnvelopePoints {
  const p = rect();
  switch (name) {
    case 'bowTie':
      // Top and bottom midpoints pulled inward: widest at the four corners,
      // pinched at the waist.
      p[1] = { x: 0.5, y: 0.25 };
      p[5] = { x: 0.5, y: 0.75 };
      break;
    case 'bubble':
      // Corners in, edge midpoints out: a lens with a domed top and bottom.
      p[0] = { x: 0.15, y: 0.15 };
      p[2] = { x: 0.85, y: 0.15 };
      p[4] = { x: 0.85, y: 0.85 };
      p[6] = { x: 0.15, y: 0.85 };
      p[1] = { x: 0.5, y: -0.15 };
      p[3] = { x: 1.15, y: 0.5 };
      p[5] = { x: 0.5, y: 1.15 };
      p[7] = { x: -0.15, y: 0.5 };
      break;
    case 'arch':
      // Both edges bowed upward by the same amount: the word rises in the
      // middle and keeps its weight.
      p[1] = { x: 0.5, y: -0.2 };
      p[5] = { x: 0.5, y: 0.8 };
      break;
    case 'wave':
      // A shear: the top edge slid right, the sides following at half.
      p[0] = { x: 0.15, y: 0 };
      p[1] = { x: 0.65, y: 0 };
      p[2] = { x: 1.15, y: 0 };
      p[3] = { x: 1.075, y: 0.5 };
      p[7] = { x: 0.075, y: 0.5 };
      break;
    case 'rectangle':
    case 'custom':
      break;
  }
  return p as unknown as EnvelopePoints;
}

/** A ready-made envelope warp, for a palette button or a preset click. */
export function envelopeWarp(name: EnvelopePreset): TextWarp {
  return { kind: 'envelope', preset: name, points: envelopePreset(name) };
}

/** True when a warp is the eight-point envelope rather than a formula preset. */
export function isEnvelopeWarp(warp: TextWarp | undefined): warp is Extract<TextWarp, { kind: 'envelope' }> {
  return warp?.kind === 'envelope';
}

// ── Date formats ─────────────────────────────────────────────────────────────

export interface DateFormat {
  /** The id IS the pattern, so a Customily token string maps across as-is. */
  id: DateFormatId;
  pattern: string;
  /** Rendered against Tuesday 14 October 2025, as Customily's own dropdown shows it. */
  example: string;
}

/** The reference day Customily's dropdown was read against. */
export const DATE_FORMAT_EXAMPLE_DAY = '2025-10-14';

/** The sixteen presets, in the order Customily lists them. */
export const DATE_FORMATS: readonly DateFormat[] = [
  { id: 'MM-dd-yy', pattern: 'MM-dd-yy', example: '10-14-25' },
  { id: 'MM.dd.yyyy', pattern: 'MM.dd.yyyy', example: '10.14.2025' },
  { id: 'MM.dd.yy', pattern: 'MM.dd.yy', example: '10.14.25' },
  { id: 'dd/MM/yyyy', pattern: 'dd/MM/yyyy', example: '14/10/2025' },
  { id: 'dd-MM-yy', pattern: 'dd-MM-yy', example: '14-10-25' },
  { id: 'dd.MM.yyyy', pattern: 'dd.MM.yyyy', example: '14.10.2025' },
  { id: 'dd.MM.yy', pattern: 'dd.MM.yy', example: '14.10.25' },
  { id: 'yyyy-MM-dd', pattern: 'yyyy-MM-dd', example: '2025-10-14' },
  { id: 'dd MMM yyyy', pattern: 'dd MMM yyyy', example: '14 Oct 2025' },
  { id: 'MMM dd, yyyy', pattern: 'MMM dd, yyyy', example: 'Oct 14, 2025' },
  { id: 'yyyy MMM dd', pattern: 'yyyy MMM dd', example: '2025 Oct 14' },
  { id: 'dd-MMM-yyyy', pattern: 'dd-MMM-yyyy', example: '14-Oct-2025' },
  { id: 'dd MMMM yyyy', pattern: 'dd MMMM yyyy', example: '14 October 2025' },
  { id: 'MMMM dd, yyyy', pattern: 'MMMM dd, yyyy', example: 'October 14, 2025' },
  { id: 'yyyy MMMM dd', pattern: 'yyyy MMMM dd', example: '2025 October 14' },
  { id: 'EEEE, MMMM dd, yyyy', pattern: 'EEEE, MMMM dd, yyyy', example: 'Tuesday, October 14, 2025' },
];

/** The preset a stored `dateFormat` names, if it is one of the sixteen. */
export function dateFormatOf(id: string | undefined): DateFormat | undefined {
  if (!id) return undefined;
  return DATE_FORMATS.find((f) => f.id === id);
}

/**
 * Reads a date answer without a timezone shift.
 *
 * A bare `2025-10-14` parsed by `new Date` is MIDNIGHT UTC, which is the 13th
 * anywhere west of Greenwich — a birthday plaque printed one day early. The
 * date-only form is therefore read as a local calendar day.
 */
function parseDate(value: string | Date): Date | undefined {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? undefined : value;
  const text = String(value).trim();
  if (text === '') return undefined;
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (ymd) return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
  const dmy = /^(\d{1,2})[./](\d{1,2})[./](\d{4})$/.exec(text);
  if (dmy) return new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

const pad = (n: number, width: number): string => String(n).padStart(width, '0');

const named = (date: Date, locale: string, options: Intl.DateTimeFormatOptions): string => {
  try {
    return new Intl.DateTimeFormat(locale, { ...options, timeZone: undefined }).format(date);
  } catch {
    return new Intl.DateTimeFormat('en', options).format(date);
  }
};

const TOKENS = /EEEE|EEE|MMMM|MMM|MM|M|dd|d|yyyy|yy/g;

/**
 * Formats a date answer through one of the presets (or any pattern in the same
 * token language: `yyyy yy MMMM MMM MM M dd d EEEE EEE`).
 *
 * Anything that is not a date — the shopper typed free text into a field bound
 * as a date, or the format is unknown — comes back untouched rather than as
 * `Invalid Date` on a printed plaque.
 */
export function formatDate(
  value: string | Date | null | undefined,
  format?: string,
  locale = 'en',
): string {
  if (value == null) return '';
  const raw = value instanceof Date ? value.toISOString() : String(value);
  if (!format) return raw;
  const date = parseDate(value);
  if (!date) return raw;

  return format.replace(TOKENS, (token) => {
    switch (token) {
      case 'yyyy':
        return pad(date.getFullYear(), 4);
      case 'yy':
        return pad(date.getFullYear() % 100, 2);
      case 'MMMM':
        return named(date, locale, { month: 'long' });
      case 'MMM':
        return named(date, locale, { month: 'short' });
      case 'MM':
        return pad(date.getMonth() + 1, 2);
      case 'M':
        return String(date.getMonth() + 1);
      case 'dd':
        return pad(date.getDate(), 2);
      case 'd':
        return String(date.getDate());
      case 'EEEE':
        return named(date, locale, { weekday: 'long' });
      case 'EEE':
        return named(date, locale, { weekday: 'short' });
      default:
        return token;
    }
  });
}
